import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { ArrowLeft, Trophy } from 'lucide-react';

const Leaderboard = ({ onBack }) => {
  const { leaderboard, fetchLeaderboard, startGame } = useGame();

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const getMedalIcon = (index) => {
    if (index === 0) return <span className="text-2xl">🥇</span>;
    if (index === 1) return <span className="text-2xl">🥈</span>;
    if (index === 2) return <span className="text-2xl">🥉</span>;
    return <span className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-fredoka text-sm">{index + 1}</span>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const handlePlay = () => {
    onBack();
    startGame('emoji');
  };

  return (
    <motion.div 
      className="min-h-screen flex flex-col px-6 py-8"
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -100 }}
    >
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          data-testid="back-button"
          onClick={onBack}
          className="w-12 h-12 rounded-full bg-white border-2 border-slate-100 shadow-sm flex items-center justify-center hover:bg-slate-50 transition-colors"
        >
          <ArrowLeft size={24} className="text-[#293241]" />
        </button>
        <div className="flex items-center gap-3">
          <Trophy className="text-[#FFD23F]" size={32} />
          <h1 className="font-fredoka text-3xl text-[#293241]">Leaderboard</h1>
        </div>
      </div>

      {/* Leaderboard List */}
      <div className="flex-1 max-w-lg mx-auto w-full">
        {leaderboard.length === 0 ? (
          <motion.div
            className="text-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="font-quicksand text-lg text-[#293241]/60">
              No scores yet.
              <br />
              Be the first!
            </p>
          </motion.div>
        ) : (
          <div className="space-y-3" data-testid="leaderboard-list">
            {leaderboard.map((entry, index) => (
              <motion.div
                key={index}
                className={`bg-white rounded-2xl p-4 border-2 ${
                  index === 0 ? 'border-[#FFD23F] shadow-lg' : 
                  index === 1 ? 'border-slate-300' : 
                  index === 2 ? 'border-amber-600/30' : 
                  'border-slate-100'
                } flex items-center gap-4`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                data-testid={`leaderboard-entry-${index}`}
              >
                {/* Rank */}
                <div className="flex-shrink-0">
                  {getMedalIcon(index)}
                </div>

                {/* Player info */}
                <div className="flex-1 min-w-0">
                  <p className="font-fredoka text-lg text-[#293241] truncate">
                    {entry.player_name}
                  </p>
                  <p className="text-sm text-[#293241]/50 font-quicksand">
                    {entry.rounds_completed} rounds • {formatDate(entry.created_at)}
                  </p>
                </div>

                {/* Score */}
                <div className="flex-shrink-0 text-right">
                  <p className={`font-fredoka text-2xl ${
                    index === 0 ? 'text-[#FFD23F]' : 
                    index === 1 ? 'text-slate-500' : 
                    index === 2 ? 'text-amber-600' : 
                    'text-[#3D5A80]'
                  }`}>
                    {entry.points}
                  </p>
                  <p className="text-xs text-[#293241]/40 font-quicksand uppercase">
                    points
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Play Button */}
      <motion.div
        className="mt-8 max-w-lg mx-auto w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <button
          data-testid="play-from-leaderboard-button"
          className="btn-primary w-full flex items-center justify-center gap-3"
          onClick={handlePlay}
        >
          Play Now
        </button>
      </motion.div>
    </motion.div>
  );
};

export default Leaderboard;
